"use client";

import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { isDateInWatchDogRange, type WatchDogDateRange } from "@/lib/date";
import { EstateOffer, MembershipInfo, UserInfo, WatchDog } from "@/types";
import {
  deleteWatchDogCompletely,
  dismissLoadingToast,
  editResult,
  editWatchDogActivity,
  fetchResultsByOffest,
  fetchWatchDogs,
  showLoadingToast,
} from "@/utils";
import { usePathname } from "next/navigation";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

type WatchDogContextType = {
  userInfoGlobal: UserInfo | null;
  isInitialLoading: boolean;
  selectedDog: WatchDog | null;
  setSelectedDog: (dog: WatchDog | null) => void;
  isLoadingWatchDogs: boolean;
  isLoadingResults: boolean;
  isLoadingAllResults: boolean;
  isCompactView: boolean;
  setIsCompactView: React.Dispatch<React.SetStateAction<boolean>>;
  sortState: { sortBy: string; ascending: boolean };
  setSortState: React.Dispatch<
    React.SetStateAction<{ sortBy: "date" | "price" | "pricePerSquareMeter" | "softiqIndex"; ascending: boolean }>
  >;
  handleSortChange: (newSortBy: "date" | "price" | "pricePerSquareMeter" | "softiqIndex") => void;
  sortedEstateOffers: EstateOffer[];
  estateOffers: EstateOffer[] | null;
  dateRange: WatchDogDateRange;
  setDateRange: React.Dispatch<React.SetStateAction<WatchDogDateRange>>;
  hasSelectedDogResults: boolean;
  hasDateRangeResults: boolean;
  membershipInfo: MembershipInfo | null;
  getWatchDogs: (resetSelectedWatchDog: boolean) => Promise<void>;
  setWatchDogAsActive: (watchDogActivityState: boolean) => Promise<void>;
  deleteWatchDog: () => Promise<void>;
  editSpecificResult: (
    id: number,
    isFavorite: boolean,
    doHide: boolean,
  ) => Promise<void>;
  isFavoriteView: boolean;
  setIsFavoriteView: React.Dispatch<React.SetStateAction<boolean>>;
  watchdogs: WatchDog[] | null;
  existingDogsCount: number;
  isDisabled: boolean;
  INITIAL_COUNT: number;
  visibleCount: number;
  setVisibleCount: React.Dispatch<React.SetStateAction<number>>;
  handleShowMore: () => void;
  handleShowLess: () => void;
  handleStop: () => void;
  stopRequestedRef: React.MutableRefObject<boolean>;
  setDidFetch: React.Dispatch<React.SetStateAction<boolean>>;
  setWatchDogs: React.Dispatch<React.SetStateAction<WatchDog[] | null>>;
  setEstateOffers: React.Dispatch<React.SetStateAction<EstateOffer[] | null>>;
};

const defaultContext: WatchDogContextType = {
  userInfoGlobal: null,
  isInitialLoading: false,
  selectedDog: null,
  setSelectedDog: () => {},
  isLoadingWatchDogs: true,
  isLoadingResults: true,
  isLoadingAllResults: false,
  isCompactView: false,
  setIsCompactView: () => {},
  sortState: { sortBy: "date", ascending: false },
  setSortState: () => {},
  handleSortChange: () => {},
  sortedEstateOffers: [],
  estateOffers: null,
  dateRange: "all",
  setDateRange: () => {},
  hasSelectedDogResults: false,
  hasDateRangeResults: false,
  membershipInfo: null,
  getWatchDogs: async () => {},
  setWatchDogAsActive: async () => {},
  deleteWatchDog: async () => {},
  editSpecificResult: async () => {},
  isFavoriteView: false,
  setIsFavoriteView: () => {},
  watchdogs: [],
  existingDogsCount: 0,
  isDisabled: false,
  INITIAL_COUNT: 30,
  visibleCount: 0,
  setVisibleCount: () => {},
  handleShowMore: () => {},
  handleShowLess: () => {},
  handleStop: () => {},
  stopRequestedRef: { current: false },
  setDidFetch: () => {},
  setWatchDogs: () => {},
  setEstateOffers: () => {},
};

const WatchDogContext = createContext<WatchDogContextType>(defaultContext);

export const WatchDogProvider = ({ children }: { children: ReactNode }) => {
  const { userInfoGlobal, setErrorMessage } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      membershipDogOne: "hlídacího psa",
      membershipDogsFew: "hlídací psy",
      membershipDogsMany: "hlídacích psů",
      stopLoading: "Zastavit načítání",
      allResultsLoaded: "Všechny výsledky načteny",
      loadingDataFor: "Získávám data za {month} {year}",
      months: [
        "leden",
        "únor",
        "březen",
        "duben",
        "květen",
        "červen",
        "červenec",
        "srpen",
        "září",
        "říjen",
        "listopad",
        "prosinec",
      ],
    },
    en: {
      membershipDogOne: "watchdog",
      membershipDogsFew: "watchdogs",
      membershipDogsMany: "watchdogs",
      stopLoading: "Stop loading",
      allResultsLoaded: "All results loaded",
      loadingDataFor: "Loading data for {month} {year}",
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    },
  }[language];
  const { loginSignal } = useSessionContext();
  const pathname = usePathname();
  const [isLoadingWatchDogs, setIsLoadingWatchDogs] = useState(
    defaultContext.isLoadingWatchDogs,
  );
  const [isLoadingResults, setIsLoadingResults] = useState(
    defaultContext.isLoadingResults,
  );
  const [isLoadingAllResults, setIsLoadingAllResults] = useState(
    defaultContext.isLoadingAllResults,
  );
  const [isInitialLoading, setIsInitialLoading] = useState(
    defaultContext.isInitialLoading,
  );
  const [estateOffers, setEstateOffers] = useState<EstateOffer[] | null>(null);
  const [isCompactView, setIsCompactView] = useState(
    defaultContext.isCompactView,
  );
  const [isFavoriteView, setIsFavoriteView] = useState(
    defaultContext.isFavoriteView,
  );
  const [sortState, setSortState] = useState<{
    sortBy: "date" | "price" | "pricePerSquareMeter" | "softiqIndex";
    ascending: boolean;
  }>({
    sortBy: "date",
    ascending: false,
  });
  const [sortedEstateOffers, setSortedEstateOffers] = useState<EstateOffer[]>(
    [],
  );
  const [dateRange, setDateRange] = useState<WatchDogDateRange>("all");
  const INITIAL_COUNT = 20;
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(
    null,
  );
  const [selectedDog, setSelectedDog] = useState<WatchDog | null>(null);
  const [watchdogs, setWatchDogs] = useState<WatchDog[] | null>(null);
  const [stopRequested, setStopRequested] = useState(false);

  const membershipMap: Record<string, MembershipInfo> = {
    BASIC: { maxWatchDogs: 1, maxWatchDogsString: copy.membershipDogOne },
    STANDARD: { maxWatchDogs: 2, maxWatchDogsString: copy.membershipDogsFew },
    PREMIUM: { maxWatchDogs: 3, maxWatchDogsString: copy.membershipDogsFew },
    PROFESSIONAL: {
      maxWatchDogs: 5,
      maxWatchDogsString: copy.membershipDogsMany,
    },
  };

  const existingDogsCount = watchdogs?.length ?? 0;

  const isDisabled =
    membershipInfo != null && existingDogsCount >= membershipInfo.maxWatchDogs;

  const setMembershipInfoDetails = () => {
    const info = membershipMap[userInfoGlobal?.membership ?? ""];
    if (info) {
      const maxWatchDogs = userInfoGlobal?.maxWatchDogs ?? info.maxWatchDogs;
      setMembershipInfo({
        maxWatchDogs,
        maxWatchDogsString:
          maxWatchDogs === 0
            ? copy.membershipDogsMany
            : info.maxWatchDogsString,
      });
    }
  };

  const handleSortChange = (newSortBy: "date" | "price" | "pricePerSquareMeter" | "softiqIndex") => {
    setSortState((prev) => {
      if (prev.sortBy === newSortBy) {
        // same sort type → toggle ascending
        return { ...prev, ascending: !prev.ascending };
      } else {
        // switched sort type → default to ascending
        return { sortBy: newSortBy, ascending: true };
      }
    });
  };

  const selectedDogOffers = useMemo(
    () =>
      selectedDog && estateOffers
        ? estateOffers.filter(
            (offer) => offer.watchdogEntity.id === selectedDog.id,
          )
        : [],
    [estateOffers, selectedDog],
  );

  const dateRangeOffers = useMemo(
    () =>
      selectedDogOffers.filter((offer) =>
        isDateInWatchDogRange(offer.createdAt, dateRange),
      ),
    [dateRange, selectedDogOffers],
  );

  const hasSelectedDogResults = selectedDogOffers.length > 0;
  const hasDateRangeResults = dateRangeOffers.length > 0;

  const fetchAndProcessOffers = () => {
    let offers = dateRangeOffers;

    if (isFavoriteView) {
      offers = offers.filter((offer) => offer.isFavorite);
    }

    if (sortState.sortBy === "date") {
      offers = offers.slice().sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortState.ascending ? dateA - dateB : dateB - dateA;
      });
    } else if (sortState.sortBy === "price") {
      offers = offers
          .filter((offer) => {
            if (!offer.price || offer.price === "Neuvedeno"  || offer.price === "0") {
              return false;
            }

            const price = parseFloat(
                offer.price.replace(/[^0-9.-]+/g, "")
            );

            return price !== 0;
          })
          .slice()
          .sort((a, b) => {
            const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, ""));
            const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, ""));

            return sortState.ascending
                ? priceA - priceB
                : priceB - priceA;
      });
    } else if (sortState.sortBy === "pricePerSquareMeter") {
      offers = offers
          .filter((offer) => offer.pricePerSquareMeter != null)
          .slice()
          .sort((a, b) => {
            const priceSquareA = parseFloat(
                a.pricePerSquareMeter.replace(/[^0-9.-]+/g, "")
            );

            const priceSquareB = parseFloat(
                b.pricePerSquareMeter.replace(/[^0-9.-]+/g, "")
            );

            return sortState.ascending
                ? priceSquareA - priceSquareB
                : priceSquareB - priceSquareA;
      });
    }    else if (sortState.sortBy === "softiqIndex") {
    offers = offers
        .filter((offer) => offer.softiqIndex != null)
        .slice()
        .sort((a, b) => {
          const softiqIndexA = a.softiqIndex ?? 0;
          const softiqIndexB = b.softiqIndex ?? 0;

          return sortState.ascending
              ? softiqIndexA - softiqIndexB
              : softiqIndexB - softiqIndexA;
        });
  }

    setSortedEstateOffers(offers);
  };

  useEffect(() => {
    fetchAndProcessOffers();
  }, [dateRangeOffers, sortState, isFavoriteView]);

  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
  }, [dateRange]);
  const [didFetch, setDidFetch] = useState(false);
  const isWatchDogPage = pathname === "/hlidaci-pes";
  const isLoggedInHomepage = pathname === "/" && userInfoGlobal != null;

  useEffect(() => {
    if (isLoggedInHomepage) {
      setSelectedDog(null);
    }
  }, [isLoggedInHomepage]);

  useEffect(() => {
    if (isWatchDogPage && selectedDog == null && watchdogs?.length) {
      setSelectedDog(watchdogs[0]);
    }
  }, [isWatchDogPage, selectedDog, watchdogs]);

  const stopRequestedRef = useRef(false);
  const progressToastIdRef = useRef<string | number | undefined>(undefined);
  const isWatchDogPageActiveRef = useRef(isWatchDogPage);

  useEffect(() => {
    stopRequestedRef.current = stopRequested;
  }, [stopRequested]);

  useEffect(() => {
    isWatchDogPageActiveRef.current = isWatchDogPage;

    if (!isWatchDogPage) {
      dismissProgressToast();
    }
  }, [isWatchDogPage]);

  const handleStop = () => {
    setStopRequested(true);
  };

  const showProgressToast = (message: string) => {
    if (!isWatchDogPageActiveRef.current) {
      return;
    }

    progressToastIdRef.current = showLoadingToast(message, {
      id: progressToastIdRef.current,
      duration: Infinity,
      dismissible: false,
      action: {
        label: copy.stopLoading,
        onClick: handleStop,
      },
      classNames: {
        actionButton:
          "!bg-softiq-primary !text-white hover:!bg-softiq-primary-shade-600",
      },
    });
  };

  const dismissProgressToast = () => {
    if (progressToastIdRef.current === undefined) {
      return;
    }
    toast.success(copy.allResultsLoaded);
    dismissLoadingToast(progressToastIdRef.current);
    progressToastIdRef.current = undefined;
  };

  useEffect(() => {
    if (
      !isWatchDogPage ||
      !userInfoGlobal?.isActive ||
      didFetch ||
      watchdogs == null
    ) {
      return;
    }

    if (watchdogs.length === 0) {
      setIsLoadingResults(false);
      setIsLoadingAllResults(false);
      dismissProgressToast();
      return;
    }

    const fetchWithDelay = async () => {
      let i = 1;
      let doContinue = true;
      setIsLoadingAllResults(true);
      setStopRequested(false);
      try {
        let currentMonth = new Date().getMonth() + 1;
        let currentYear = new Date().getFullYear();
        while (doContinue && !stopRequestedRef.current) {
          let month = currentMonth - i + 1;
          let year = currentYear;
          while (month <= 0) {
            month += 12;
            year -= 1;
          }

          const monthName = copy.months[month - 1];
          showProgressToast(
            copy.loadingDataFor
              .replace("{month}", monthName)
              .replace("{year}", String(year)),
          );

          doContinue = await getResultsByOffset(i);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          i++;
        }
      } finally {
        setIsLoadingAllResults(false);
        dismissProgressToast();
      }
    };

    (async () => {
      setDidFetch(true);
      setIsLoadingResults(true);
      try {
        await getResultsByOffset(0);
      } finally {
        setIsLoadingResults(false);
      }

      await fetchWithDelay();
    })();
  }, [watchdogs, didFetch, isWatchDogPage, userInfoGlobal?.isActive]);

  useEffect(() => {
    if (
      !isLoggedInHomepage ||
      !userInfoGlobal?.isActive ||
      watchdogs == null ||
      estateOffers != null
    ) {
      return;
    }

    setIsLoadingResults(true);
    Promise.all([getResultsByOffset(0), getResultsByOffset(1)])
      .then(() => setEstateOffers((current) => current ?? []))
      .finally(() => setIsLoadingResults(false));
  }, [isLoggedInHomepage, watchdogs, estateOffers, userInfoGlobal?.isActive]);

  useEffect(() => {
    if (userInfoGlobal != null) {
      setIsInitialLoading(false);
      getWatchDogs(true);
      setMembershipInfoDetails();
    } else {
      // User logged out: reset local WatchDog-related state to initial values
      setSelectedDog(null);
      setEstateOffers(null);
      setSortedEstateOffers([]);
      setDateRange("all");
      setWatchDogs(null);
      setIsLoadingWatchDogs(false);
      setIsLoadingResults(false);
      setIsLoadingAllResults(false);
      dismissProgressToast();
      setStopRequested(false);
      stopRequestedRef.current = false;
      setDidFetch(false);
    }
  }, [userInfoGlobal]);

  // Reset state on every successful login (even when switching users without null userInfoGlobal)
  useEffect(() => {
    if (loginSignal > 0) {
      // reset to initial
      setSelectedDog(null);
      setEstateOffers(null);
      setSortedEstateOffers([]);
      setDateRange("all");
      setWatchDogs(null);
      setIsLoadingWatchDogs(false);
      setIsLoadingResults(false);
      setIsLoadingAllResults(false);
      dismissProgressToast();
      setStopRequested(false);
      stopRequestedRef.current = false;
      setDidFetch(false);

      // then fetch fresh data for the (newly) logged-in user
      if (userInfoGlobal != null) {
        setIsInitialLoading(false);
        getWatchDogs(true);
        setMembershipInfoDetails();
      }
    }
  }, [loginSignal]);

  const getWatchDogs = async (resetSelectedWatchDog: boolean) => {
    try {
      if (!userInfoGlobal?.token) {
        return Promise.resolve();
      }
      setIsLoadingWatchDogs(true);
      const payload: Record<string, any> = {
        token: userInfoGlobal.token,
        email: userInfoGlobal.email,
      };
      const response = await fetchWatchDogs(payload, userInfoGlobal.token);

      if (response.status === 200) {
        // Map the raw response data to the WatchDog array, extracting the necessary fields
        const watchdogsArray: WatchDog[] = response.data.data.map(
          (watchdog: any) => ({
            id: watchdog.id,
            client: watchdog.client,
            isFirstSyncCompleted: watchdog.isFirstSyncCompleted,
            isEnabled: watchdog.isEnabled,
            doSendUpdated: watchdog.doSendUpdated,
            doEmailReport: watchdog.doEmailReport,
            emailReportFrequency: watchdog.emailReportFrequency,
            emailReportSetting: watchdog.emailReportSetting,
            sendOfferAs: watchdog.sendOfferAs,

            realityUrl:
              typeof watchdog.realityUrl === "string"
                ? watchdog.realityUrl.split(";").map((s: string) => s.trim())
                : [],
            estateType: watchdog.estateType,
            offerType: watchdog.offerType,

            flatCategory:
              typeof watchdog.flatCategory === "string"
                ? watchdog.flatCategory.split(";").map((s: string) => s.trim())
                : [],

            homeCategory:
              typeof watchdog.homeCategory === "string"
                ? watchdog.homeCategory.split(";").map((s: string) => s.trim())
                : [],

            landCategory:
              typeof watchdog.landCategory === "string"
                ? watchdog.landCategory.split(";").map((s: string) => s.trim())
                : [],

            comercialCategory:
              typeof watchdog.comercialCategory === "string"
                ? watchdog.comercialCategory
                    .split(";")
                    .map((s: string) => s.trim())
                : [],

            otherCategory:
              typeof watchdog.otherCategory === "string"
                ? watchdog.otherCategory.split(";").map((s: string) => s.trim())
                : [],
            country: watchdog.country,
            exactAddress: watchdog.exactAddress,
            searchAddress: watchdog.searchAddress,
            inRange: watchdog.inRange,
            region: watchdog.region,
            cities: watchdog.cities,

            proximity:
              typeof watchdog.proximity === "string"
                ? watchdog.proximity.split(";").map((s: string) => s.trim())
                : [],

            ownership:
              typeof watchdog.ownership === "string"
                ? watchdog.ownership.split(";").map((s: string) => s.trim())
                : [],

            estateCondition:
              typeof watchdog.estateCondition === "string"
                ? watchdog.estateCondition
                    .split(";")
                    .map((s: string) => s.trim())
                : [],

            accessories:
              typeof watchdog.accessories === "string"
                ? watchdog.accessories.split(";").map((s: string) => s.trim())
                : [],

            buildingMaterial:
              typeof watchdog.buildingMaterial === "string"
                ? watchdog.buildingMaterial
                    .split(";")
                    .map((s: string) => s.trim())
                : [],
            floorFrom: watchdog.floorFrom,
            floorTo: watchdog.floorTo,
            usableAreaFrom: watchdog.usableAreaFrom,
            usableAreaTo: watchdog.usableAreaTo,
            priceFrom: watchdog.priceFrom,
            priceTo: watchdog.priceTo,
            smartSearch: watchdog.smartSearch,
            energyRating:
              typeof watchdog.energyRating === "string"
                ? watchdog.energyRating.split(";").map((s: string) => s.trim())
                : [],
            age: watchdog.age,
            hasVideo: watchdog.hasVideo,
            has3DView: watchdog.has3DView,
            hasLayout: watchdog.hasLayout,
            urlSreality: watchdog.urlSreality,
            urlBazos: watchdog.urlBazos,
            urlBezrealitky: watchdog.urlBezrealitky,
            urlIdnesReality: watchdog.urlIdnesReality,
          }),
        );
        setWatchDogs(watchdogsArray);

        if (Array.isArray(watchdogsArray) && watchdogsArray.length > 0) {
          if (isLoggedInHomepage) {
            const selectedWatchDogId = selectedDog?.id;
            setSelectedDog(
              selectedWatchDogId == null
                ? null
                : (watchdogsArray.find(
                    (dog) => dog.id === selectedWatchDogId,
                  ) ?? null),
            );
          } else if (resetSelectedWatchDog) {
            setSelectedDog(watchdogsArray[0]);
          } else {
            const selectedWatchDogId = selectedDog?.id;
            const foundDog = watchdogsArray.find(
              (dog) => dog.id === selectedWatchDogId,
            );
            setSelectedDog(foundDog ?? watchdogsArray[0]);
          }
        } else {
          setSelectedDog(null);
        }
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoadingWatchDogs(false);
    }
  };

  const getResultsByOffset = async (offset: number) => {
    if (!userInfoGlobal?.token && !userInfoGlobal?.isActive) {
      return false;
    }

    try {
      const payload = {
        token: userInfoGlobal.token,
        email: userInfoGlobal.email,
        data: {
          offset,
        },
      };

      const response = await fetchResultsByOffest(
        payload,
        userInfoGlobal.token,
      );

      if (response.status === 200) {
        let offers = response.data.data;
        if (offers.length > 0) {
          setEstateOffers((prev) => {
            const offersById = new Map(
              [...(prev ?? []), ...offers].map((offer) => [offer.id, offer]),
            );
            return Array.from(offersById.values());
          });
          return true;
        } else {
          return false;
        }
      }
      return false;
    } catch (err) {
      setErrorMessage(String(err));
      return false;
    }
  };

  const setWatchDogAsActive = async (watchDogActivityState: boolean) => {
    try {
      if (!userInfoGlobal?.token || !selectedDog) {
        return;
      }
      const payload = {
        email: userInfoGlobal!.email,
        token: userInfoGlobal!.token,
        data: {
          watchDogId: selectedDog.id,
          watchDogActivityState: watchDogActivityState,
        },
      };
      const response = await editWatchDogActivity(
        payload,
        userInfoGlobal.token,
      );

      if (response.status !== 200) return;
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      getWatchDogs(false);
    }
  };

  const deleteWatchDog = async () => {
    try {
      if (!userInfoGlobal?.token || !selectedDog) {
        return;
      }
      const deletedWatchDogId = selectedDog.id;
      const payload = {
        email: userInfoGlobal!.email,
        token: userInfoGlobal!.token,
        data: {
          watchDogId: deletedWatchDogId,
        },
      };
      const response = await deleteWatchDogCompletely(
        payload,
        userInfoGlobal.token,
      );

      if (response.status !== 200) return;

      setEstateOffers((currentEstateOffers) => {
        const remainingEstateOffers =
          currentEstateOffers?.filter(
            (offer) => offer.watchdogEntity.id !== deletedWatchDogId,
          ) ?? [];

        return remainingEstateOffers.length > 0 ? remainingEstateOffers : null;
      });
      setSortedEstateOffers([]);
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      await getWatchDogs(true);
    }
  };

  const editSpecificResult = async (
    id: number,
    isFavorite: boolean,
    doHide: boolean,
  ) => {
    if (!userInfoGlobal?.token) {
      return;
    }

    const payload = {
      email: userInfoGlobal!.email,
      token: userInfoGlobal!.token,
      data: {
        watchDogEstateId: id,
        isFavorite: isFavorite,
        doHide: doHide,
      },
    };

    try {
      await editResult(payload, userInfoGlobal.token);
      setEstateOffers((currentEstateOffers) => {
        if (!currentEstateOffers) {
          return null;
        }

        return doHide
          ? currentEstateOffers.filter((offer) => offer.id !== id)
          : currentEstateOffers.map((offer) =>
              offer.id === id ? { ...offer, isFavorite, doHide } : offer,
            );
      });
    } catch (err) {
      setErrorMessage(String(err));
    }
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + INITIAL_COUNT);
  };
  const handleShowLess = () => {
    setVisibleCount((prev) => prev - INITIAL_COUNT);
  };

  return (
    <WatchDogContext.Provider
      value={{
        userInfoGlobal,
        isInitialLoading,
        selectedDog,
        setSelectedDog,
        isLoadingWatchDogs,
        isLoadingResults,
        isLoadingAllResults,
        isCompactView,
        setIsCompactView,
        sortState,
        setSortState,
        handleSortChange,
        sortedEstateOffers,
        estateOffers,
        dateRange,
        setDateRange,
        hasSelectedDogResults,
        hasDateRangeResults,
        membershipInfo,
        getWatchDogs,
        setWatchDogAsActive,
        deleteWatchDog,
        editSpecificResult,
        isFavoriteView,
        setIsFavoriteView,
        watchdogs,
        existingDogsCount,
        isDisabled,
        INITIAL_COUNT,
        visibleCount,
        setVisibleCount,
        handleShowMore,
        handleShowLess,
        handleStop,
        stopRequestedRef,
        setDidFetch,
        setWatchDogs,
        setEstateOffers,
      }}
    >
      {children}
    </WatchDogContext.Provider>
  );
};

export const useWatchDogContext = () => useContext(WatchDogContext);
